import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.js'
import { PICTOGRAMS, makePictogramUrl } from '../constants/pictograms.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './FormFlowBuilder.css'

const FLOW_TYPES = [
  { id: 'pre_checkin', label: 'Pre-Checkin (Web)', group: null },
  { id: 'terminal',    label: 'Flow-Auswahl',      group: 'Kiosk' },
]

const FIELD_TYPES = [
  { value: 'text',     label: 'Text' },
  { value: 'email',    label: 'E-Mail' },
  { value: 'phone',    label: 'Telefon' },
  { value: 'date',     label: 'Datum' },
  { value: 'time',     label: 'Uhrzeit' },
  { value: 'textarea', label: 'Textbereich' },
  { value: 'select',   label: 'Auswahl' },
  { value: 'checkbox', label: 'Checkbox' },
]

// Field IDs the server expects in visitorData — warn the admin if they differ
const RESERVED_FIELD_IDS = new Set([
  'firstName', 'lastName', 'company', 'email',
  'phone', 'visitDate', 'visitTime', 'licensePlate', 'reasonForVisit',
])

function generateId() {
  return Math.random().toString(36).slice(2, 10)
}

// Extracts the editable string from a label that may already be a translation object.
// If it's an object, shows the sourceLang version (fallback to 'de', then first value).
function getLabelString(label, sourceLang) {
  if (!label) return ''
  if (typeof label === 'string') return label
  return label[sourceLang] || label['de'] || Object.values(label)[0] || ''
}

// ── Field Editor ─────────────────────────────────────────────────────────────

// deliveryCheck: null on flows where the Lieferavis check cannot apply (pre-checkin),
// otherwise { enabled } — whether the tenant has the feature switched on.
function FieldEditor({ config, onChange, sourceLang, deliveryCheck = null }) {
  const rawTitle = config?.title ?? ''
  const fields   = config?.fields ?? []
  const referenceField = fields.find(f => f.isReference) ?? null
  const deliveryOn = !!config?.deliveryCheck
  const dragFieldIdx = useRef(null)

  // Display the source-language version of the title
  const titleDisplay = getLabelString(rawTitle, sourceLang)

  // When admin types a new title, store as plain string — server will (re)translate on save
  const updateTitle = (val) => onChange({ ...config, title: val })

  const updateField = (idx, patch) => {
    let next = fields.map((f, i) => i === idx ? { ...f, ...patch } : f)
    // Exactly one reference field per step: ticking a second one unticks the first
    // rather than leaving two and letting the server pick arbitrarily. The queue view
    // has a single Referenznummer column, so a second marked field has nowhere to go.
    if (patch.isReference === true) {
      next = next.map((f, i) => (i === idx ? f : { ...f, isReference: false }))
    }
    onChange({ ...config, fields: next })
  }

  const addField = () => {
    const next = [
      ...fields,
      { id: '', type: 'text', label: 'Neues Feld', required: false },
    ]
    onChange({ ...config, fields: next })
  }

  const removeField = (idx) => {
    const next = fields.filter((_, i) => i !== idx)
    onChange({ ...config, fields: next })
  }

  const handleFieldDragStart = (e, idx) => {
    dragFieldIdx.current = idx
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleFieldDragOver = (e, idx) => {
    e.preventDefault()
    if (dragFieldIdx.current === null || dragFieldIdx.current === idx) return
    const next = [...fields]
    const [dragged] = next.splice(dragFieldIdx.current, 1)
    next.splice(idx, 0, dragged)
    dragFieldIdx.current = idx
    onChange({ ...config, fields: next })
  }

  const handleFieldDrop = () => { dragFieldIdx.current = null }

  return (
    <div className="ffb-field-editor">
      <div className="ffb-field-editor-title-row">
        <label>Seitentitel</label>
        <input
          className="form-control"
          value={titleDisplay}
          onChange={e => updateTitle(e.target.value)}
          placeholder="z. B. Ihre Angaben"
        />
      </div>

      <div className="ffb-field-rows">
        {fields.map((field, idx) => (
          <div
            key={idx}
            className="ffb-field-row"
            draggable
            onDragStart={e => handleFieldDragStart(e, idx)}
            onDragOver={e => handleFieldDragOver(e, idx)}
            onDrop={handleFieldDrop}
          >
            <span className="ffb-field-drag" title="Ziehen zum Verschieben">≡</span>

            <div className="ffb-field-main">
              <div className="ffb-field-top-row">
                <input
                  className="form-control ffb-field-label-input"
                  value={getLabelString(field.label, sourceLang)}
                  onChange={e => updateField(idx, { label: e.target.value })}
                  placeholder="Feldbezeichnung"
                />

                <select
                  className="form-control ffb-field-type-select"
                  value={field.type}
                  onChange={e => updateField(idx, { type: e.target.value })}
                >
                  {FIELD_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>

                <label className="ffb-field-required-label">
                  <input
                    type="checkbox"
                    checked={field.required}
                    onChange={e => updateField(idx, { required: e.target.checked })}
                  />
                  Pflicht
                </label>

                {/* Was labelled just "Abgleich", which read as "the reference check" once
                    Lieferavis existed. Same flag (allowlistCheck), clearer name. */}
                <label className="ffb-field-required-label ffb-field-allowlist-label" title="Feld wird beim Check-in mit der Personenzulassungsliste abgeglichen">
                  <input
                    type="checkbox"
                    checked={field.allowlistCheck ?? false}
                    onChange={e => updateField(idx, { allowlistCheck: e.target.checked })}
                  />
                  Personenzulassung
                </label>

                {/* Which field carries the tenant's own order number — it may be called
                    Referenznummer, Auftragsnummer or Lieferschein, so it is marked here
                    rather than guessed from the id. Shown as its own column in der
                    Warteschlangenansicht, and the field the Lieferavis check reads. */}
                <label className="ffb-field-required-label ffb-field-allowlist-label" title="Wird in der Warteschlange als Referenznummer angezeigt und ist das Feld, das mit dem Lieferavis abgeglichen wird (nur ein Feld pro Schritt)">
                  <input
                    type="checkbox"
                    checked={field.isReference ?? false}
                    onChange={e => updateField(idx, { isReference: e.target.checked })}
                  />
                  Referenz
                </label>

                {deliveryCheck && deliveryOn && field.isReference && (
                  <span className="ffb-delivery-badge" title="Dieses Feld wird mit dem Lieferavis abgeglichen">Lieferavis</span>
                )}
              </div>

              <div className="ffb-field-id-row">
                <span className="ffb-field-id-label">Feld-ID</span>
                <input
                  className={`form-control ffb-field-id-input${!RESERVED_FIELD_IDS.has(field.id) ? ' ffb-field-id-warn' : ''}`}
                  value={field.id}
                  onChange={e => updateField(idx, { id: e.target.value.replace(/\s/g, '') })}
                  placeholder="z. B. firstName"
                  spellCheck={false}
                  list="ffb-reserved-ids"
                />
                {!RESERVED_FIELD_IDS.has(field.id) && (
                  <span
                    className="ffb-field-id-hint"
                    title="Reservierte IDs: firstName, lastName, company, email, phone, visitDate, visitTime, licensePlate, reasonForVisit"
                  >
                    ⚠ Keine Standard-ID — Daten werden als Extra-Felder gespeichert
                  </span>
                )}
              </div>

              <div className="ffb-field-id-row">
                <span className="ffb-field-id-label">Infotext <span className="ffb-label-opt">(optional)</span></span>
                <input
                  className="form-control"
                  value={getLabelString(field.hint, sourceLang)}
                  onChange={e => updateField(idx, { hint: e.target.value })}
                  placeholder="z. B. Bitte ohne Leerzeichen eingeben"
                />
              </div>

              {field.type === 'time' && (
                <div className="ffb-field-time-hours">
                  <span className="ffb-field-id-label">Öffnungszeiten</span>
                  <input
                    className="form-control"
                    type="time"
                    value={field.openFrom ?? ''}
                    onChange={e => updateField(idx, { openFrom: e.target.value })}
                    placeholder="Von"
                    title="Öffnung ab"
                  />
                  <span className="ffb-time-sep">–</span>
                  <input
                    className="form-control"
                    type="time"
                    value={field.openUntil ?? ''}
                    onChange={e => updateField(idx, { openUntil: e.target.value })}
                    placeholder="Bis"
                    title="Schließung um"
                  />
                </div>
              )}

              {field.type === 'select' && (
                <div className="ffb-field-options">
                  <span className="ffb-field-id-label">Optionen</span>
                  <div className="ffb-field-options-list">
                    {(field.options ?? []).map((opt, oi) => (
                      <div key={oi} className="ffb-field-option-row">
                        <input
                          className="form-control"
                          value={opt}
                          onChange={e => {
                            const next = [...(field.options ?? [])]
                            next[oi] = e.target.value
                            updateField(idx, { options: next })
                          }}
                          placeholder={`Option ${oi + 1}`}
                        />
                        <button
                          className="ffb-btn-icon ffb-btn-icon--danger"
                          onClick={() => {
                            const next = (field.options ?? []).filter((_, i) => i !== oi)
                            updateField(idx, { options: next })
                          }}
                        >✕</button>
                      </div>
                    ))}
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => updateField(idx, { options: [...(field.options ?? []), ''] })}
                    >+ Option hinzufügen</button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="ffb-btn-icon ffb-btn-icon--danger"
              title="Feld entfernen"
              onClick={() => removeField(idx)}
            >✕</button>
          </div>
        ))}

        {fields.length === 0 && (
          <p className="ffb-editor-empty">
            Noch keine Felder — füge das erste Feld hinzu.
          </p>
        )}
      </div>

      {/* Step-level, not per field: plenty of flows mark a Referenz only for the queue
          view and must not start blocking drivers. This tick is the explicit "and
          enforce it" — Plans/Lieferavis_Plan.md §5. Disabled until a field is marked,
          but always switchable off again. */}
      {deliveryCheck && (
        <div className="ffb-delivery-check">
          <label className="ffb-field-required-label">
            <input
              type="checkbox"
              checked={deliveryOn}
              disabled={!referenceField && !deliveryOn}
              onChange={e => onChange({ ...config, deliveryCheck: e.target.checked })}
            />
            Referenznummer mit Lieferavis abgleichen
          </label>
          {!referenceField ? (
            <p className={`ffb-delivery-check-hint${deliveryOn ? ' ffb-delivery-check-hint--warn' : ''}`}>
              {deliveryOn
                ? 'Kein Feld ist als Referenz markiert — es wird nichts geprüft.'
                : 'Bitte zuerst ein Feld als Referenz markieren.'}
            </p>
          ) : deliveryOn && (
            <p className="ffb-delivery-check-hint">
              Geprüft wird: {getLabelString(referenceField.label, sourceLang) || referenceField.id}
            </p>
          )}
          {deliveryOn && !deliveryCheck.enabled && (
            <p className="ffb-delivery-check-hint ffb-delivery-check-hint--warn">
              Lieferavis ist für diesen Mandanten unter „Funktionen" nicht eingeschaltet — es findet keine Prüfung statt.
            </p>
          )}
        </div>
      )}

      <div className="ffb-field-editor-actions">
        <button className="btn btn-ghost btn-sm" onClick={addField}>
          + Feld hinzufügen
        </button>
      </div>

      <datalist id="ffb-reserved-ids">
        {[...RESERVED_FIELD_IDS].map(id => <option key={id} value={id} />)}
      </datalist>
    </div>
  )
}

// ── Icon Picker ───────────────────────────────────────────────────────────────

function IconPicker({ icon, iconUrl, onChange, apiFetch, tenantId }) {
  const [open,      setOpen]      = useState(false)
  const [uploading, setUploading] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  const selectPictogram = (p) => {
    onChange({ icon: undefined, iconUrl: makePictogramUrl(p.svg) })
    setOpen(false)
  }

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const reader = new FileReader()
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1]
      try {
        const res  = await apiFetch('/api/admin/upload-icon', {
          method: 'POST',
          body:   JSON.stringify({ tenantId, fileName: file.name, fileData: base64 }),
        })
        const data = await res.json()
        if (res.ok) {
          onChange({ icon: undefined, iconUrl: data.url })
          setOpen(false)
        }
      } catch {} finally {
        setUploading(false)
        e.target.value = ''
      }
    }
    reader.readAsDataURL(file)
  }

  const activePictoId = iconUrl
    ? PICTOGRAMS.find(p => iconUrl === makePictogramUrl(p.svg))?.id
    : null

  return (
    <div className="ffb-icon-picker-wrap" ref={wrapRef}>
      <button
        type="button"
        className="ffb-icon-preview-btn"
        onClick={() => setOpen(o => !o)}
        title="Piktogramm wählen"
      >
        {iconUrl
          ? <img src={iconUrl} alt="icon" className="ffb-icon-img-preview" />
          : <span className="ffb-icon-text-preview">{icon || '▶'}</span>
        }
      </button>

      {open && (
        <div className="ffb-icon-popup">
          <div className="ffb-icon-popup-label">Piktogramm wählen</div>
          <div className="ffb-icon-grid">
            {PICTOGRAMS.map(p => (
              <button
                key={p.id}
                type="button"
                className={`ffb-icon-preset${activePictoId === p.id ? ' active' : ''}`}
                onClick={() => selectPictogram(p)}
                title={p.label}
              >
                <img src={makePictogramUrl(p.svg)} alt={p.label} />
              </button>
            ))}
          </div>
          <div className="ffb-icon-upload-row">
            <label className={`btn btn-ghost btn-sm${uploading ? ' disabled' : ''}`}>
              {uploading ? 'Hochladen…' : '📁 Eigenes Bild'}
              <input
                type="file"
                accept="image/*"
                className="ffb-file-input"
                onChange={handleUpload}
                disabled={uploading}
              />
            </label>
            {iconUrl && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => { onChange({ icon: '▶', iconUrl: undefined }); setOpen(false) }}
              >
                ✕ Entfernen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Flow Picker Editor ────────────────────────────────────────────────────────

function FlowPickerEditor({ config, onChange, onAutoSave, apiFetch, tenantId, parentFlowType = 'terminal', sourceLang = 'de' }) {
  const subFlows = config?.subFlows ?? []
  const dragIdx  = useRef(null)
  const up = (patch) => onChange({ ...config, ...patch })
  const ls = (val) => getLabelString(val, sourceLang)

  const [showAddForm, setShowAddForm] = useState(false)
  const [newMode,     setNewMode]     = useState('')
  const [newLabel,    setNewLabel]    = useState('')
  const [newIconUrl,  setNewIconUrl]  = useState(undefined)
  const [newDesc,     setNewDesc]     = useState('')

  const updateSubFlow = (idx, patch) => {
    const next = subFlows.map((sf, i) => i === idx ? { ...sf, ...patch } : sf)
    onChange({ ...config, subFlows: next })
  }

  const removeSubFlow = (idx) => {
    const next = subFlows.filter((_, i) => i !== idx)
    onChange({ ...config, subFlows: next })
  }

  const handleDragStart = (e, idx) => {
    dragIdx.current = idx
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e, idx) => {
    e.preventDefault()
    if (dragIdx.current === null || dragIdx.current === idx) return
    const next = [...subFlows]
    const [dragged] = next.splice(dragIdx.current, 1)
    next.splice(idx, 0, dragged)
    dragIdx.current = idx
    onChange({ ...config, subFlows: next })
  }

  const handleDrop = () => { dragIdx.current = null }

  const handleAddSubFlow = () => {
    if (!newMode.trim() || !newLabel.trim()) return
    const sanitizedMode = newMode.trim().replace(/\s+/g, '_').toLowerCase()
    const newSf = {
      mode:        sanitizedMode,
      flowType:    `${parentFlowType}_${sanitizedMode}`,
      label:       newLabel.trim(),
      iconUrl:     newIconUrl || undefined,
      icon:        newIconUrl ? undefined : '▶',
      description: newDesc.trim(),
      enabled:     true,
    }
    const updatedConfig = { ...config, subFlows: [...subFlows, newSf] }
    onChange(updatedConfig)
    setNewMode('')
    setNewLabel('')
    setNewIconUrl(undefined)
    setNewDesc('')
    setShowAddForm(false)
    // Auto-save so the checkin-station immediately sees the new subflow
    if (onAutoSave) onAutoSave(updatedConfig)
  }

  return (
    <div className="ffb-field-editor">
      <div className="form-group">
        <label className="ffb-editor-label">
          Titel <span className="ffb-label-opt">(leer = Standard)</span>
        </label>
        <input
          className="form-control"
          value={ls(config?.title)}
          onChange={e => up({ title: e.target.value })}
          placeholder="z. B. Willkommen"
        />
      </div>
      <div className="form-group">
        <label className="ffb-editor-label">
          Untertitel <span className="ffb-label-opt">(leer = Standard)</span>
        </label>
        <input
          className="form-control"
          value={ls(config?.subtitle)}
          onChange={e => up({ subtitle: e.target.value })}
          placeholder="z. B. Bitte wählen Sie eine Option"
        />
      </div>

      <div className="ffb-subflow-list">
        {subFlows.map((sf, idx) => (
          <div
            key={sf.mode}
            className="ffb-subflow-row"
            draggable
            onDragStart={e => handleDragStart(e, idx)}
            onDragOver={e => handleDragOver(e, idx)}
            onDrop={handleDrop}
          >
            <span className="ffb-field-drag" title="Ziehen zum Verschieben">≡</span>

            <IconPicker
              icon={sf.icon}
              iconUrl={sf.iconUrl}
              onChange={patch => updateSubFlow(idx, patch)}
              apiFetch={apiFetch}
              tenantId={tenantId}
            />

            <input
              className="form-control ffb-subflow-label-input"
              value={getLabelString(sf.label, 'de')}
              onChange={e => updateSubFlow(idx, { label: e.target.value })}
              placeholder="Label"
            />

            <input
              className="form-control ffb-subflow-desc-input"
              value={getLabelString(sf.description, 'de')}
              onChange={e => updateSubFlow(idx, { description: e.target.value })}
              placeholder="Beschreibung"
            />

            <label className="ffb-field-required-label" title="Aktiviert">
              <input
                type="checkbox"
                checked={sf.enabled}
                onChange={e => updateSubFlow(idx, { enabled: e.target.checked })}
              />
              Aktiv
            </label>

            <button
              className="ffb-btn-icon ffb-btn-icon--danger"
              title="Unterflow entfernen"
              onClick={() => removeSubFlow(idx)}
            >✕</button>
          </div>
        ))}

        {subFlows.length === 0 && (
          <p className="ffb-editor-empty">
            Keine Unterflows definiert.
          </p>
        )}
      </div>

      {showAddForm && (
        <div className="ffb-subflow-add-form">
          <div className="ffb-subflow-add-row">
            <input
              className="form-control"
              value={newMode}
              onChange={e => setNewMode(e.target.value)}
              placeholder="Bezeichner (z.B. checkin)"
            />
            <input
              className="form-control"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Label (z.B. Check-In)"
            />
            <IconPicker
              iconUrl={newIconUrl}
              onChange={({ iconUrl }) => setNewIconUrl(iconUrl)}
              apiFetch={apiFetch}
              tenantId={tenantId}
            />
            <input
              className="form-control"
              value={newDesc}
              onChange={e => setNewDesc(e.target.value)}
              placeholder="Beschreibung"
            />
          </div>
          <div className="ffb-subflow-add-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAddSubFlow}
              disabled={!newMode.trim() || !newLabel.trim()}
            >
              Hinzufügen
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowAddForm(false)}
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      <div className="ffb-field-editor-actions">
        {!showAddForm && (
          <button className="btn btn-ghost btn-sm" onClick={() => setShowAddForm(true)}>
            + Unterflow hinzufügen
          </button>
        )}
      </div>
    </div>
  )
}

// ── Repeat-for-field control ─────────────────────────────────────────────────
// Shown for every configurable step. Lets the admin pick a numeric field from
// any data_entry step in the same flow; at runtime that step will be duplicated
// N times (once per unit, e.g. passenger).

function MultiplyByControl({ config, onChange, allSteps, sourceLang }) {
  // Collect all numeric-ish field IDs from all data_entry steps in the flow
  const numericFields = allSteps
    .filter(s => s.type === 'data_entry')
    .flatMap(s => (s.config?.fields ?? []).filter(f => f.type === 'text' || f.type === 'number'))
    .map(f => ({ id: f.id, label: getLabelString(f.label, sourceLang) || f.id }))

  if (numericFields.length === 0) return null

  return (
    <div className="form-group ffb-multiply-section">
      <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-sub)' }}>
        Wiederholen für Feld{' '}
        <span className="ffb-label-opt">(leer = kein Wiederholen)</span>
      </label>
      <select
        className="form-control"
        value={config?.multiplyBy ?? ''}
        onChange={e => onChange({ ...config, multiplyBy: e.target.value || undefined })}
      >
        <option value="">— kein Wiederholen —</option>
        {numericFields.map(f => (
          <option key={f.id} value={f.id}>{f.label} ({f.id})</option>
        ))}
      </select>

      {config?.multiplyBy && (
        <>
          <label className="ffb-editor-label ffb-editor-label--block">
            Bezeichnung der Einheit{' '}
            <span className="ffb-label-opt">(z. B. Passagier, Fahrzeug, Person)</span>
          </label>
          <input
            className="form-control"
            value={config?.repeatItemLabel ?? ''}
            onChange={e => onChange({ ...config, repeatItemLabel: e.target.value || undefined })}
            placeholder="z. B. Passagier"
          />
          <p className="ffb-field-hint">
            Wird im Kiosk angezeigt als: „{config?.repeatItemLabel || '…'} 2 / 3"
          </p>
        </>
      )}

      {!config?.multiplyBy && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          Diese Seite wird im Kiosk so oft angezeigt, wie der gewählte Feldwert angibt.
        </p>
      )}
    </div>
  )
}

// ── Group control ────────────────────────────────────────────────────────────
// Shown only when "Wiederholen für Feld" is set. Lets the admin assign this
// step to a group so it's shown together with other grouped steps on each
// repetition (e.g. Sicherheitsregeln + Unterschrift, once per Person).

function GroupControl({ step, config, onChange, allSteps, pageTypes }) {
  if (!config?.multiplyBy) return null

  const labelFor = (type) => pageTypes.find(pt => pt.type === type)?.label ?? type

  // Other steps repeating for the same field — candidates for grouping
  const siblings = allSteps.filter(
    s => s.id !== step.id && s.config?.multiplyBy === config.multiplyBy
  )

  const existingGroups = [...new Set(
    siblings.map(s => s.config?.groupId).filter(Boolean)
  )]

  const groupMembers = config?.groupId
    ? siblings.filter(s => s.config?.groupId === config.groupId)
    : []

  return (
    <div className="form-group ffb-multiply-section">
      <label className="ffb-editor-label ffb-editor-label--block">
        Gruppe{' '}
        <span className="ffb-label-opt">(leer = einzeln wiederholen)</span>
      </label>
      <input
        className="form-control"
        list={`ffb-group-options-${step.id}`}
        value={config?.groupId ?? ''}
        onChange={e => onChange({ ...config, groupId: e.target.value || undefined })}
        placeholder="z. B. besucher_block"
      />
      {existingGroups.length > 0 && (
        <datalist id={`ffb-group-options-${step.id}`}>
          {existingGroups.map(g => <option key={g} value={g} />)}
        </datalist>
      )}

      {config?.groupId ? (
        groupMembers.length > 0 ? (
          <p className="ffb-field-hint">
            Wird zusammen mit {groupMembers.map(s => labelFor(s.type)).join(', ')} pro Wiederholung angezeigt.
          </p>
        ) : (
          <p className="ffb-field-hint">
            Noch keine andere Seite in dieser Gruppe — vergib denselben Gruppennamen bei
            einer weiteren Seite mit demselben Wiederholungsfeld, um sie zusammen anzuzeigen.
          </p>
        )
      ) : (
        <p className="ffb-field-hint">
          Seiten mit demselben Gruppennamen und Wiederholungsfeld werden pro Wiederholung
          nacheinander angezeigt (z. B. Sicherheitsregeln + Unterschrift pro Person).
        </p>
      )}
    </div>
  )
}

// ── QR Checkin Editor ─────────────────────────────────────────────────────────

function QrCheckinEditor({ config, onChange, stepType, sourceLang = 'de' }) {
  const mode       = config?.scannerMode ?? 'serial'
  const isCheckout = stepType === 'checkout_scan'
  const up = (patch) => onChange({ ...config, ...patch })
  const ls = (val) => getLabelString(val, sourceLang)

  return (
    <div className="ffb-field-editor">

      {/* Scanner mode */}
      <div className="form-group">
        <label className="ffb-editor-label">Scanner-Modus</label>
        <select className="form-control" value={mode} onChange={e => up({ scannerMode: e.target.value })}>
          <option value="serial">Handscanner / USB-Barcodescanner</option>
          <option value="webcam">Webcam (Kamera)</option>
          <option value="both">Beides (Handscanner + Webcam)</option>
        </select>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
          {mode === 'serial' && 'USB-HID-Barcodescanner simuliert Tastatureingabe. Kein Treiber nötig.'}
          {mode === 'webcam' && 'Nutzt die eingebaute oder angeschlossene Kamera zum Scannen.'}
          {mode === 'both'   && 'Besucher können per Handscanner oder Kamera scannen.'}
        </p>
      </div>

      {/* Title */}
      <div className="form-group">
        <label className="ffb-editor-label">
          Titel <span className="ffb-label-opt">(leer = Standard)</span>
        </label>
        <input
          className="form-control"
          value={ls(config?.title)}
          onChange={e => up({ title: e.target.value })}
          placeholder={isCheckout ? 'z. B. Auf Wiedersehen!' : 'z. B. QR-Code scannen'}
        />
      </div>

      {/* Hint */}
      <div className="form-group">
        <label className="ffb-editor-label">
          Hinweistext <span className="ffb-label-opt">(leer = Standard)</span>
        </label>
        <textarea
          className="form-control"
          rows={2}
          value={ls(config?.hint)}
          onChange={e => up({ hint: e.target.value })}
          placeholder={isCheckout
            ? 'z. B. Bitte scannen Sie Ihren QR-Code zum Auschecken.'
            : 'z. B. Bitte scannen Sie Ihren QR-Code mit dem Handscanner.'}
        />
      </div>

      {/* Re-checkin success texts */}
      {!isCheckout && (
        <>
          <div className="form-group">
            <label className="ffb-editor-label">
              Erfolgs-Titel <span className="ffb-label-opt">(leer = Standard)</span>
            </label>
            <input
              className="form-control"
              value={ls(config?.successTitle)}
              onChange={e => up({ successTitle: e.target.value })}
              placeholder="z. B. Willkommen zurück!"
            />
          </div>
          <div className="form-group">
            <label className="ffb-editor-label">
              Erfolgs-Hinweis <span className="ffb-label-opt">(leer = Standard)</span>
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={ls(config?.successHint)}
              onChange={e => up({ successHint: e.target.value })}
              placeholder="z. B. Ihr Re-Check-In wird verarbeitet…"
            />
          </div>
        </>
      )}

      {/* Checkout-only: success texts */}
      {isCheckout && (
        <>
          <div className="form-group">
            <label className="ffb-editor-label">
              Erfolgs-Titel <span className="ffb-label-opt">(leer = Standard)</span>
            </label>
            <input
              className="form-control"
              value={ls(config?.successTitle)}
              onChange={e => up({ successTitle: e.target.value })}
              placeholder="z. B. Auf Wiedersehen!"
            />
          </div>
          <div className="form-group">
            <label className="ffb-editor-label">
              Erfolgs-Nachricht <span className="ffb-label-opt">(leer = Standard)</span>
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={ls(config?.successMessage)}
              onChange={e => up({ successMessage: e.target.value })}
              placeholder="z. B. Ihr Check-Out wurde erfasst."
            />
          </div>
        </>
      )}

    </div>
  )
}

// ── Direct Call Editor ────────────────────────────────────────────────────────
// Direktanruf: the kiosk calls one phone-book contact automatically. Only the
// contact's id is stored — step configs are public (form-config), so the number
// stays in the phone book. How many tries a visitor gets is set per tenant on
// Telefonkontakte, not here.

function DirectCallEditor({ config, onChange, sourceLang = 'de', tenantId, apiFetch }) {
  const up = (patch) => onChange({ ...config, ...patch })
  const ls = (val) => getLabelString(val, sourceLang)

  const [contacts, setContacts] = useState(null)   // null = loading
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (!tenantId) return
    let cancelled = false
    setLoadError(false)
    apiFetch(`/api/admin/sip-contacts/${tenantId}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (!cancelled) setContacts(Array.isArray(data?.contacts) ? data.contacts : []) })
      .catch(() => { if (!cancelled) { setContacts([]); setLoadError(true) } })
    return () => { cancelled = true }
    // apiFetch is deliberately not a dependency: AuthContext recreates it on every
    // provider render, which would reload the list while the owner is editing.
  }, [tenantId])

  const selected = contacts?.find(c => c.id === config?.contactId) || null
  // The chosen contact was deleted from the phone book (only possible outside the
  // dashboard — deleting a contact in use is blocked) or the id is stale.
  const missing  = contacts && config?.contactId && !selected

  return (
    <div className="ffb-field-editor">

      <div className="form-group">
        <label className="ffb-editor-label">Kontakt</label>
        {contacts === null ? (
          <div><span className="spinner spinner-sm" /></div>
        ) : loadError ? (
          <p className="ffb-field-hint">Telefonkontakte konnten nicht geladen werden.</p>
        ) : contacts.length === 0 ? (
          <p className="ffb-field-hint">
            Noch keine Telefonkontakte für diesen Mandanten — bitte zuerst unter „Telefonkontakte“ anlegen.
          </p>
        ) : (
          <select
            className="form-control"
            value={selected ? selected.id : ''}
            onChange={e => up({ contactId: e.target.value || null })}
          >
            <option value="">— Kontakt wählen —</option>
            {contacts.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.sip_target}){c.enabled ? '' : ' — inaktiv'}
              </option>
            ))}
          </select>
        )}
        {!config?.contactId && contacts?.length > 0 && (
          <p className="ffb-field-hint ffb-field-hint--warn">
            Ohne Kontakt ruft die Seite niemanden an — der Besucher sieht „Kein Kontakt konfiguriert“ und kann weiter.
          </p>
        )}
        {missing && (
          <p className="ffb-field-hint ffb-field-hint--warn">
            Der gewählte Kontakt existiert nicht mehr. Bitte einen anderen wählen.
          </p>
        )}
        {selected && !selected.enabled && (
          <p className="ffb-field-hint ffb-field-hint--warn">
            Dieser Kontakt ist inaktiv — die Seite ruft niemanden an, bis er unter „Telefonkontakte“ wieder aktiviert ist.
          </p>
        )}
        <p className="ffb-field-hint">
          Name und Nummer werden unter „Telefonkontakte“ gepflegt, ebenso die Anzahl der Anrufversuche.
        </p>
      </div>

      <div className="form-group">
        <label className="ffb-editor-label">
          Titel <span className="ffb-label-opt">(leer = Standard)</span>
        </label>
        <input
          className="form-control"
          value={ls(config?.title)}
          onChange={e => up({ title: e.target.value })}
          placeholder="z. B. Bitte melden Sie sich an der Pforte"
        />
      </div>

      <div className="form-group">
        <label className="ffb-editor-label">
          Untertitel <span className="ffb-label-opt">(optional)</span>
        </label>
        <input
          className="form-control"
          value={ls(config?.subtitle)}
          onChange={e => up({ subtitle: e.target.value })}
          placeholder="z. B. Sie werden automatisch mit dem Empfang verbunden."
        />
      </div>

    </div>
  )
}

// ── Document Scan Editor ──────────────────────────────────────────────────────

function DocumentScanEditor({ config, onChange, sourceLang = 'de' }) {
  const up = (patch) => onChange({ ...config, ...patch })
  const ls = (val) => getLabelString(val, sourceLang)

  return (
    <div className="ffb-field-editor">

      <div className="form-group">
        <label className="ffb-editor-label">Kamera</label>
        <select className="form-control" value={config?.facingMode ?? 'environment'} onChange={e => up({ facingMode: e.target.value })}>
          <option value="environment">Rückkamera (Dokument scannen)</option>
          <option value="user">Frontkamera (Selfie / Portrait)</option>
        </select>
      </div>

      <div className="form-group">
        <label className="ffb-editor-label">
          Titel <span className="ffb-label-opt">(leer = Standard)</span>
        </label>
        <input
          className="form-control"
          value={ls(config?.title)}
          onChange={e => up({ title: e.target.value })}
          placeholder="z. B. Ausweis fotografieren"
        />
      </div>

      <div className="form-group">
        <label className="ffb-editor-label">
          Hinweistext <span className="ffb-label-opt">(leer = Standard)</span>
        </label>
        <textarea
          className="form-control"
          rows={2}
          value={ls(config?.hint)}
          onChange={e => up({ hint: e.target.value })}
          placeholder="z. B. Bitte halten Sie Ihren Ausweis vor die Kamera."
        />
      </div>

      <div className="form-group">
        <label className="ffb-field-required-label ffb-editor-label">
          <input
            type="checkbox"
            checked={config?.required !== false}
            onChange={e => up({ required: e.target.checked })}
          />
          Pflichtfeld (Besucher kann nicht überspringen)
        </label>
      </div>

    </div>
  )
}

function ConfirmationEditor({ config, onChange, sourceLang = 'de', tenantId }) {
  const { apiFetch } = useAuth()
  const up = (patch) => onChange({ ...config, ...patch })
  const ls = (val) => getLabelString(val, sourceLang)

  // The barriers the kiosk can actually match for this tenant's flows (by label, at
  // the stations the tenant is assigned to). Replaces free text, where a typo silently
  // opened nothing — or a same-named barrier at another station. null = not loaded.
  const [barrierLabels, setBarrierLabels] = useState(null)
  useEffect(() => {
    if (!tenantId) return
    apiFetch(`/api/admin/barrier-labels/${tenantId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setBarrierLabels(Array.isArray(d) ? d : []))
      .catch(() => setBarrierLabels([]))
  }, [tenantId, apiFetch])

  const selectedLabels = config?.openBarrierLabels || []
  const knownLabels = new Set((barrierLabels || []).map(b => b.label))

  return (
    <div className="ffb-field-editor">
      <p className="ffb-field-hint">Welche Aktionen sollen dem Besucher auf dem Abschlussbildschirm angeboten werden?</p>

      <div className="form-group">
        <label className="ffb-field-required-label ffb-editor-label">
          <input
            type="checkbox"
            checked={config?.showPrint !== false}
            onChange={e => up({ showPrint: e.target.checked })}
          />
          🖨️ Drucken
        </label>
      </div>

      <div className="form-group">
        <label className="ffb-field-required-label ffb-editor-label">
          <input
            type="checkbox"
            checked={config?.showEmail !== false}
            onChange={e => up({ showEmail: e.target.checked })}
          />
          ✉️ Per E-Mail senden
        </label>
      </div>

      <div className="form-group">
        <label className="ffb-field-required-label ffb-editor-label">
          <input
            type="checkbox"
            checked={config?.showPrintAndEmail !== false}
            onChange={e => up({ showPrintAndEmail: e.target.checked })}
          />
          🖨️✉️ Drucken &amp; per E-Mail senden
        </label>
      </div>

      <div className="form-group">
        <label className="ffb-field-required-label ffb-editor-label">
          <input
            type="checkbox"
            checked={config?.showContactPhone === true}
            onChange={e => up({ showContactPhone: e.target.checked })}
          />
          📞 Ansprechpartner-Telefonnummer anzeigen
        </label>
        <p className="ffb-field-hint">
          Zeigt Name und Telefonnummer der Person, die der Besucher zuvor im Schritt „Ansprechpartner-Auswahl" ausgewählt hat.
          Wirkt nur, wenn dieser Flow auch einen solchen Schritt enthält.
        </p>
      </div>

      <div className="form-group">
        <label className="ffb-editor-label">
          Zusätzliche Hinweise <span className="ffb-label-opt">(optional)</span>
        </label>
        <textarea
          className="form-control"
          rows={3}
          value={ls(config?.instructions)}
          onChange={e => up({ instructions: e.target.value })}
          placeholder="z. B. Hinweise zum weiteren Vorgehen, Wartebereich, Ansprechpartner, etc."
        />
        <p className="ffb-field-hint">Wird dem Besucher zusätzlich auf dem Abschlussbildschirm angezeigt.</p>
      </div>

      <div className="form-group">
        <label className="ffb-editor-label">🚧 Schranke(n) automatisch öffnen <span className="ffb-label-opt">(optional)</span></label>
        <p className="ffb-field-hint">
          Öffnet die ausgewählte(n) Schranke(n), sobald dieser Abschlussbildschirm nach einem erfolgreichen Check-in angezeigt wird —
          an dem Terminal, an dem eingecheckt wird. Zur Auswahl stehen die Schranken der Stationen, denen dieser Mandant zugewiesen ist.
          Eine Schranke mit getrenntem „Zu"-Relais schließt das Terminal nach der eingestellten Zeit wieder.
        </p>
        {barrierLabels !== null && barrierLabels.length === 0 && selectedLabels.length === 0 && (
          <p className="ffb-field-hint">Für die Stationen dieses Mandanten sind noch keine Schranken hinterlegt.</p>
        )}
        {selectedLabels.map((label, idx) => (
          <div key={idx} className="ffb-barrier-label-row">
            <select
              className="form-control"
              value={label}
              onChange={e => {
                const next = [...selectedLabels]
                next[idx] = e.target.value
                up({ openBarrierLabels: next })
              }}
            >
              <option value="">— Schranke auswählen —</option>
              {(barrierLabels || []).map(b => (
                <option key={b.label} value={b.label}>
                  {b.label}{b.stations.length ? ` (${b.stations.join(', ')})` : ''}
                </option>
              ))}
              {/* A label saved earlier that no station has (any more) stays visible
                  instead of silently turning into "nothing selected". */}
              {label && barrierLabels !== null && !knownLabels.has(label) && (
                <option value={label}>{label} — an keiner Station vorhanden</option>
              )}
            </select>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => up({ openBarrierLabels: selectedLabels.filter((_, i) => i !== idx) })}
            >
              ×
            </button>
          </div>
        ))}
        {selectedLabels.some(l => l && barrierLabels !== null && !knownLabels.has(l)) && (
          <p className="ffb-field-hint ffb-barrier-warning">
            Mindestens eine ausgewählte Schranke gibt es an keiner Station dieses Mandanten — sie wird beim Check-in nicht geöffnet.
          </p>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => up({ openBarrierLabels: [...selectedLabels, ''] })}
        >
          + Schranke hinzufügen
        </button>
      </div>
    </div>
  )
}

function SignatureEditor({ config, onChange }) {
  const up = (patch) => onChange({ ...config, ...patch })

  return (
    <div className="ffb-field-editor">
      <p className="ffb-field-hint">Soll der Besucher vor dem Unterschreiben Vor- und Nachnamen eingeben?</p>

      <div className="form-group">
        <label className="ffb-field-required-label ffb-editor-label">
          <input
            type="checkbox"
            checked={config?.collectName === true}
            onChange={e => up({ collectName: e.target.checked })}
          />
          📝 Vor- und Nachname abfragen
        </label>
        <p className="ffb-field-hint">
          Wird automatisch mit abgefragt, sobald unten „Wiederholen für Feld" gesetzt ist
          und der eingegebene Wert größer als 1 ist (z. B. mehrere Personen) — diese
          Checkbox erzwingt die Abfrage zusätzlich auch bei nur einer Person.
        </p>
      </div>
    </div>
  )
}

// ── Add Page Picker ──────────────────────────────────────────────────────────

function AddPagePicker({ pageTypes, existingSteps, onAdd, onClose }) {
  // Filter out singletons that are already in the flow
  const usedSingletonTypes = new Set(
    existingSteps
      .filter(s => pageTypes.find(pt => pt.type === s.type && pt.singleton))
      .map(s => s.type)
  )

  const available = pageTypes.filter(pt => {
    if (pt.fixed) return false           // fixed pages can't be manually added
    if (pt.singleton && usedSingletonTypes.has(pt.type)) return false
    return true
  })

  return (
    <div className="ffb-picker-overlay" onClick={onClose}>
      <div className="ffb-picker" onClick={e => e.stopPropagation()}>
        <div className="ffb-picker-header">
          <h3 className="ffb-picker-title">Seite hinzufügen</h3>
          <button className="ffb-btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="ffb-picker-list">
          {available.length === 0 ? (
            <p className="ffb-picker-empty">Alle verfügbaren Seiten sind bereits im Flow.</p>
          ) : (
            available.map(pt => (
              <button
                key={pt.type}
                className="ffb-picker-item"
                onClick={() => onAdd(pt)}
              >
                <span className="ffb-picker-item-label">{pt.label}</span>
                <span className="ffb-picker-item-desc">{pt.description}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ── Info Page Picker ─────────────────────────────────────────────────────────
// Shown when adding a data_entry step — lets the user clone an existing
// DataEntry config from another sub-flow, or start with a blank one.
function DataEntryPicker({ tenantId, apiFetch, onSelect, onCreate, onClose }) {
  const [steps,   setSteps]   = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  useEffect(() => {
    apiFetch(`/api/admin/data-entry-steps/${tenantId}`)
      .then(r => r.json())
      .then(data => { setSteps(Array.isArray(data) ? data : []); setLoading(false) })
      .catch(() => { setError('Vorlagen konnten nicht geladen werden'); setLoading(false) })
  }, [tenantId])

  const flowLabel = (flowType) => {
    if (!flowType) return ''
    return flowType.replace(/^terminal_/, '')
  }

  const fieldSummary = (config) => {
    const fields = config?.fields ?? []
    const names  = fields.slice(0, 3).map(f => getLabelString(f.label, 'de') || f.id)
    return names.join(', ') + (fields.length > 3 ? ` +${fields.length - 3}` : '')
  }

  return (
    <div className="ffb-picker-overlay" onClick={onClose}>
      <div className="ffb-picker" onClick={e => e.stopPropagation()}>
        <div className="ffb-picker-header">
          <h3 className="ffb-picker-title">Datenerfassungsseite wählen</h3>
          <button className="ffb-btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="ffb-picker-list">
          <button className="ffb-picker-item ffb-picker-item--new" onClick={onCreate}>
            <span className="ffb-picker-item-label">+ Neue leere Seite</span>
            <span className="ffb-picker-item-desc">Felder von Grund auf neu konfigurieren</span>
          </button>

          {loading && <p className="ffb-picker-empty">Lade Vorlagen…</p>}
          {error   && <p className="ffb-picker-empty">{error}</p>}

          {!loading && !error && steps?.length === 0 && (
            <p className="ffb-picker-empty">Noch keine anderen Datenerfassungsseiten vorhanden.</p>
          )}

          {!loading && steps?.map((s, i) => (
            <button
              key={i}
              className="ffb-picker-item"
              onClick={() => onSelect(s.config)}
            >
              <span className="ffb-picker-item-label">
                {getLabelString(s.config?.title, 'de') || 'Datenerfassung'}
                <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>
                  ({flowLabel(s.flowType)})
                </span>
              </span>
              <span className="ffb-picker-item-desc">{fieldSummary(s.config)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Dynamic info page editor ─────────────────────────────────────────────────
// Rules for a dynamic_info_page step: an ordered list, first match wins, plus a
// mandatory fallback. Each rule is a FLAT list of conditions combined with all/any —
// there are deliberately no nested groups, because "A and (B or C)" is expressible as
// two rules and flat rules are much harder to misconfigure by accident.

const CONDITION_SOURCES = [
  { value: 'field',     label: 'Formularfeld' },
  { value: 'time',      label: 'Ankunftszeit' },
  { value: 'flow_mode', label: 'Gewählter Ablauf' },
]

const FIELD_OPERATORS = [
  { value: 'equals',       label: 'ist gleich',     needsValue: true  },
  { value: 'not_equals',   label: 'ist ungleich',   needsValue: true  },
  { value: 'contains',     label: 'enthält',        needsValue: true  },
  { value: 'one_of',       label: 'ist eines von',  needsValue: true, list: true },
  { value: 'is_empty',     label: 'ist leer',       needsValue: false },
  { value: 'is_not_empty', label: 'ist nicht leer', needsValue: false },
]

const WEEKDAYS = [
  { value: 'mon', label: 'Mo' }, { value: 'tue', label: 'Di' }, { value: 'wed', label: 'Mi' },
  { value: 'thu', label: 'Do' }, { value: 'fri', label: 'Fr' }, { value: 'sat', label: 'Sa' },
  { value: 'sun', label: 'So' },
]

const newRule = () => ({
  id: `r${Date.now()}${Math.floor(Math.random() * 1000)}`,
  match: 'all',
  conditions: [{ source: 'field', key: '', operator: 'equals', value: '' }],
  target: { pageId: null, pageName: '' },
})

function ConditionRow({ cond, onChange, onRemove, fields, isPreCheckin, canRemove }) {
  const up = (patch) => onChange({ ...cond, ...patch })

  // Switching source resets the rest of the row — the operators and value shapes are
  // not interchangeable, and carrying them over produces silently-never-matching rules.
  const changeSource = (source) => {
    if (source === 'field')     return onChange({ source, key: '', operator: 'equals', value: '' })
    if (source === 'flow_mode') return onChange({ source, key: null, operator: 'equals', value: '' })
    return onChange({
      source: 'time', key: 'time_of_day', operator: 'between',
      basis: isPreCheckin ? 'planned' : 'actual', value: ['', ''],
    })
  }

  const opDef = FIELD_OPERATORS.find(o => o.value === cond.operator)

  return (
    <div className="ffb-dyn-condition">
      <select className="form-control ffb-dyn-select"
              value={cond.source} onChange={e => changeSource(e.target.value)}>
        {CONDITION_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>

      {cond.source === 'field' && (
        <>
          <select className="form-control ffb-dyn-select"
                  value={cond.key ?? ''} onChange={e => up({ key: e.target.value })}>
            <option value="">— Feld wählen —</option>
            {fields.map(f => <option key={f.id} value={f.id}>{f.label} ({f.id})</option>)}
          </select>
          <select className="form-control ffb-dyn-select"
                  value={cond.operator} onChange={e => up({ operator: e.target.value })}>
            {FIELD_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {opDef?.needsValue && (
            <input className="form-control ffb-dyn-value"
                   value={Array.isArray(cond.value) ? cond.value.join(', ') : (cond.value ?? '')}
                   onChange={e => up({ value: opDef.list
                     ? e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                     : e.target.value })}
                   placeholder={opDef.list ? 'Wert1, Wert2, …' : 'Wert'} />
          )}
        </>
      )}

      {cond.source === 'time' && (
        <>
          <select className="form-control ffb-dyn-select"
                  value={cond.key} onChange={e => up({
                    key: e.target.value,
                    operator: e.target.value === 'weekday' ? 'one_of' : 'between',
                    value: e.target.value === 'weekday' ? [] : ['', ''],
                  })}>
            <option value="time_of_day">Uhrzeit zwischen</option>
            <option value="weekday">Wochentag</option>
          </select>

          {/* In pre-checkin only the PLANNED arrival exists — a pre-checkin can happen
              days before the visit, so "now" is meaningless there. Locked, not hidden,
              so the reason stays visible. */}
          <select className="form-control ffb-dyn-select"
                  value={cond.basis || 'actual'}
                  disabled={isPreCheckin}
                  onChange={e => up({ basis: e.target.value })}>
            <option value="actual">tatsächliche Ankunft</option>
            <option value="planned">geplante Ankunft</option>
          </select>

          {cond.key === 'time_of_day' ? (
            <span className="ffb-dyn-range">
              <input type="time" className="form-control ffb-dyn-time"
                     value={cond.value?.[0] ?? ''}
                     onChange={e => up({ value: [e.target.value, cond.value?.[1] ?? ''] })} />
              <span className="ffb-dyn-sep">–</span>
              <input type="time" className="form-control ffb-dyn-time"
                     value={cond.value?.[1] ?? ''}
                     onChange={e => up({ value: [cond.value?.[0] ?? '', e.target.value] })} />
            </span>
          ) : (
            <span className="ffb-dyn-weekdays">
              {WEEKDAYS.map(d => {
                const on = (cond.value || []).includes(d.value)
                return (
                  <button key={d.value} type="button"
                          className={`ffb-dyn-day${on ? ' ffb-dyn-day--on' : ''}`}
                          onClick={() => up({ value: on
                            ? cond.value.filter(v => v !== d.value)
                            : [...(cond.value || []), d.value] })}>
                    {d.label}
                  </button>
                )
              })}
            </span>
          )}
        </>
      )}

      {cond.source === 'flow_mode' && (
        <>
          <select className="form-control ffb-dyn-select"
                  value={cond.operator} onChange={e => up({ operator: e.target.value })}>
            <option value="equals">ist gleich</option>
            <option value="one_of">ist eines von</option>
          </select>
          <input className="form-control ffb-dyn-value"
                 value={Array.isArray(cond.value) ? cond.value.join(', ') : (cond.value ?? '')}
                 onChange={e => up({ value: cond.operator === 'one_of'
                   ? e.target.value.split(',').map(v => v.trim()).filter(Boolean)
                   : e.target.value })}
                 placeholder="z. B. lieferung" />
        </>
      )}

      <button type="button" className="ffb-btn-icon ffb-btn-icon--danger"
              title="Bedingung entfernen" disabled={!canRemove} onClick={onRemove}>🗑</button>
    </div>
  )
}

function DynamicInfoPageEditor({ config, onChange, allSteps, sourceLang, tenantId, apiFetch, flowType }) {
  const [pages, setPages] = useState([])
  // Empty for a tenant without the Zielorte feature, which is also how the target
  // picker decides whether to offer destinations at all.
  const [destinations, setDestinations] = useState([])
  const isPreCheckin = flowType === 'pre_checkin'

  useEffect(() => {
    if (!tenantId) return
    apiFetch(`/api/admin/info-pages/${tenantId}`)
      .then(r => r.json())
      .then(d => setPages(Array.isArray(d) ? d : []))
      .catch(() => setPages([]))

    apiFetch(`/api/admin/destinations/${tenantId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setDestinations(Array.isArray(d) ? d : []))
      .catch(() => setDestinations([]))
  }, [tenantId])

  const rules    = Array.isArray(config?.rules) ? config.rules : []
  const fallback = config?.fallback ?? { pageId: null, pageName: '' }

  // Every field from every data_entry step in this flow — rules match on what the
  // visitor typed, so any field is fair game (not just numeric ones).
  const fields = allSteps
    .filter(s => s.type === 'data_entry')
    .flatMap(s => (s.config?.fields ?? []))
    .map(f => ({ id: f.id, label: getLabelString(f.label, sourceLang) || f.id }))

  const hasTimeField = allSteps
    .filter(s => s.type === 'data_entry')
    .some(s => (s.config?.fields ?? []).some(f => f.type === 'time'))

  const setRules = (next) => onChange({ ...config, rules: next })
  const patchRule = (idx, patch) => setRules(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  const moveRule  = (idx, dir) => {
    const next = [...rules]
    const to = idx + dir
    if (to < 0 || to >= next.length) return
    ;[next[idx], next[to]] = [next[to], next[idx]]
    setRules(next)
  }

  const pickPage = (pageId) => {
    const p = pages.find(x => x.id === pageId)
    return { pageId: pageId || null, pageName: p?.name ?? '' }
  }

  /**
   * A rule's target is EITHER a page or a destination, never both — the two keys are
   * mutually exclusive so the resolver cannot be handed an ambiguous rule.
   *
   * The value carries a 'd:' prefix so one <select> can offer both lists without the
   * ids colliding.
   */
  const pickTarget = (value) => {
    if (value.startsWith('d:')) {
      const id = value.slice(2)
      const d = destinations.find(x => x.id === id)
      return { destinationId: id, destinationName: d?.displayName ?? '' }
    }
    return pickPage(value)
  }

  const targetValue = (rule) =>
    rule.target?.destinationId ? `d:${rule.target.destinationId}` : (rule.target?.pageId ?? '')

  // Warn rather than block: a planned time-of-day rule in a flow that collects no time
  // field can never match. Blocking would be wrong (the admin may be about to add the
  // field), but silence would leave a rule that quietly does nothing.
  const plannedTimeWithoutField = !hasTimeField && rules.some(r =>
    (r.conditions || []).some(c => c.source === 'time' && c.key === 'time_of_day'
      && (c.basis || 'actual') === 'planned'))

  const resolution = config?.resolution === 'manual' ? 'manual' : 'rules'
  const manual     = config?.manual ?? { candidates: [], timeoutSeconds: 60 }

  const toggleCandidate = (pageId) => {
    const current = Array.isArray(manual.candidates) ? manual.candidates : []
    onChange({
      ...config,
      manual: {
        ...manual,
        candidates: current.includes(pageId)
          ? current.filter(id => id !== pageId)
          : [...current, pageId],
      },
    })
  }

  return (
    <div className="ffb-field-editor">
      {/* Mode switch. Manual is terminal-only: a pre-checkin can happen days early
          with nobody watching a dashboard, and the visitor closes the tab straight
          afterwards, so there would be nothing to wait for and nowhere to deliver a
          later decision. (That case is served by email instead.) */}
      <div className="form-group">
        <label className="ffb-editor-label">Wie wird die Seite bestimmt?</label>
        <select
          className="form-control ffb-dyn-mode"
          value={isPreCheckin ? 'rules' : resolution}
          disabled={isPreCheckin}
          onChange={e => onChange({ ...config, resolution: e.target.value })}
        >
          <option value="rules">Regelbasiert — automatisch aus den Eingaben</option>
          <option value="manual">Manuelle Zuweisung — Personal wählt im Dashboard</option>
        </select>
        {isPreCheckin && (
          <p className="ffb-dyn-hint">
            Im Vorab-Check-in ist nur die regelbasierte Auswahl möglich — er kann Tage
            vor dem Besuch stattfinden, ohne dass jemand auf das Dashboard schaut.
          </p>
        )}
      </div>

      {resolution === 'manual' && !isPreCheckin ? (
        <>
          <p className="ffb-dyn-intro">
            Der Besucher erscheint im Dashboard, sobald er seine Daten eingegeben hat —
            das Personal kann die Seite also schon zuweisen, <strong>bevor</strong> er
            diesen Schritt erreicht. Erreicht er ihn ohne Zuweisung, wartet er kurz und
            sieht danach die Rückfallseite.
          </p>

          <div className="form-group">
            <label className="ffb-editor-label">
              Auswählbare Seiten{' '}
              <span className="ffb-label-opt">(keine Auswahl = alle Seiten des Mandanten)</span>
            </label>
            <div className="ffb-dyn-candidates">
              {pages.length === 0 && (
                <p className="ffb-dyn-hint">Noch keine Informationsseiten vorhanden.</p>
              )}
              {pages.map(p => (
                <label key={p.id} className="ffb-dyn-candidate">
                  <input
                    type="checkbox"
                    checked={(manual.candidates ?? []).includes(p.id)}
                    onChange={() => toggleCandidate(p.id)}
                  />
                  <span>{p.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="ffb-editor-label">
              Wartezeit am Terminal{' '}
              <span className="ffb-label-opt">(Sekunden, danach Rückfallseite)</span>
            </label>
            <input
              type="number"
              min="5"
              max="600"
              className="form-control ffb-input-sm"
              value={manual.timeoutSeconds ?? 60}
              onChange={e => onChange({
                ...config,
                manual: { ...manual, timeoutSeconds: Number(e.target.value) || 60 },
              })}
            />
            <p className="ffb-dyn-hint">
              Nach dieser Zeit geht es für den Besucher weiter — eine verpasste Zuweisung
              ist ärgerlich, ein blockiertes Terminal ist ein Ausfall.
            </p>
          </div>
        </>
      ) : (
      <>
      <p className="ffb-dyn-intro">
        Regeln werden <strong>von oben nach unten</strong> geprüft — die erste zutreffende
        Regel bestimmt die angezeigte Seite. Trifft keine Regel zu, wird die Rückfallseite
        angezeigt.
      </p>

      {rules.map((rule, idx) => (
        <div className="ffb-dyn-rule" key={rule.id ?? idx}>
          <div className="ffb-dyn-rule-head">
            <span className="ffb-dyn-rule-index">Regel {idx + 1}</span>
            <select className="form-control ffb-dyn-match"
                    value={rule.match ?? 'all'}
                    onChange={e => patchRule(idx, { match: e.target.value })}>
              <option value="all">alle Bedingungen</option>
              <option value="any">mindestens eine</option>
            </select>
            <span className="ffb-dyn-rule-actions">
              <button type="button" className="ffb-btn-icon" title="Nach oben"
                      disabled={idx === 0} onClick={() => moveRule(idx, -1)}>↑</button>
              <button type="button" className="ffb-btn-icon" title="Nach unten"
                      disabled={idx === rules.length - 1} onClick={() => moveRule(idx, 1)}>↓</button>
              <button type="button" className="ffb-btn-icon ffb-btn-icon--danger" title="Regel entfernen"
                      onClick={() => setRules(rules.filter((_, i) => i !== idx))}>🗑</button>
            </span>
          </div>

          {(rule.conditions ?? []).map((cond, ci) => (
            <ConditionRow
              key={ci}
              cond={cond}
              fields={fields}
              isPreCheckin={isPreCheckin}
              canRemove={(rule.conditions ?? []).length > 1}
              onChange={next => patchRule(idx, {
                conditions: rule.conditions.map((c, i) => (i === ci ? next : c)),
              })}
              onRemove={() => patchRule(idx, {
                conditions: rule.conditions.filter((_, i) => i !== ci),
              })}
            />
          ))}

          <button type="button" className="ffb-dyn-add-cond"
                  onClick={() => patchRule(idx, {
                    conditions: [...(rule.conditions ?? []),
                      { source: 'field', key: '', operator: 'equals', value: '' }],
                  })}>
            + Bedingung
          </button>

          <div className="form-group">
            <label className="ffb-editor-label">
              {destinations.length > 0 ? 'Dann hierhin schicken' : 'Dann diese Seite zeigen'}
            </label>
            <select className="form-control"
                    value={targetValue(rule)}
                    onChange={e => patchRule(idx, { target: pickTarget(e.target.value) })}>
              <option value="">— auswählen —</option>
              {destinations.length > 0 && (
                <optgroup label="Zielorte">
                  {destinations.map(d => (
                    <option key={d.id} value={`d:${d.id}`}>
                      {d.displayName}{d.capacity != null ? ` (${d.capacity} Pl.)` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="Informationsseiten">
                {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </optgroup>
            </select>
            {/* Which KIND of target this is, stated on screen. The option label alone
                is not enough: a destination and an info page can share a name (an admin
                naturally calls both "Rampe A"), the group heading is only visible while
                the dropdown is open, and the two behave completely differently — only a
                destination is capacity-checked. Picking the wrong one looks correct and
                silently produces a ladder that never skips. */}
            {destinations.length > 0 && rule.target?.destinationId && (
              <p className="ffb-dyn-hint">
                <strong>Zielort.</strong> Ist er belegt, wird die Regel übersprungen und
                die nächste geprüft. Greift keine, gilt die Rückfallseite.
              </p>
            )}
            {destinations.length > 0 && rule.target?.pageId && (
              <p className="ffb-dyn-hint">
                <strong>Informationsseite.</strong> Keine Kapazitätsprüfung — diese Regel
                greift immer, auch wenn ein gleichnamiger Zielort belegt ist.
              </p>
            )}
          </div>
        </div>
      ))}

      <button type="button" className="ffb-dyn-add-rule" onClick={() => setRules([...rules, newRule()])}>
        + Regel hinzufügen
      </button>
      </>
      )}

      {/* Required in BOTH modes: rules mode uses it when nothing matches, manual mode
          when the wait times out. Either way it is what stops a visitor being stranded. */}
      {/* Tried before the page below when no rule matched. A real holding area rather
          than a page means the overflow is COUNTED — otherwise a yard full of drivers
          sent to a "Lageplan" page leaves the Wartebereich showing 0/10, the
          near-capacity alert never fires, and the board understates the pressure. */}
      {destinations.length > 0 && (
        <div className="form-group ffb-dyn-fallback">
          <label className="ffb-editor-label">Rückfall-Zielort <span className="ffb-dyn-hint">(optional)</span></label>
          <select className="form-control"
                  value={fallback.destinationId ?? ''}
                  onChange={e => {
                    const id = e.target.value || null
                    const d = destinations.find(x => x.id === id)
                    onChange({
                      ...config,
                      // Kept alongside the page, not instead of it: if this is full
                      // too, the page is what stops the visitor being left with
                      // nothing.
                      fallback: { ...fallback, destinationId: id, destinationName: d?.displayName ?? '' },
                    })
                  }}>
            <option value="">— keiner —</option>
            {destinations.map(d => (
              <option key={d.id} value={d.id}>
                {d.displayName}{d.capacity != null ? ` (${d.capacity} Pl.)` : ' (unbegrenzt)'}
              </option>
            ))}
          </select>
          <p className="ffb-dyn-hint">
            Greift keine Regel, wird zuerst hierhin zugewiesen — sinnvoll für einen
            Wartebereich. Nur wenn auch dieser belegt ist, erscheint die Rückfallseite.
            Ein Zielort <strong>ohne Kapazitätsgrenze</strong> ist nie voll und fängt
            damit jeden Fall ab.
          </p>
        </div>
      )}

      <div className="form-group ffb-dyn-fallback">
        <label className="ffb-editor-label">
          Rückfallseite <span className="ffb-dyn-required">(erforderlich)</span>
        </label>
        <select className="form-control"
                value={fallback.pageId ?? ''}
                onChange={e => onChange({
                  ...config,
                  // Spread so picking a page does not wipe the fallback destination
                  // above — the two are a pair, not alternatives.
                  fallback: { ...fallback, ...pickPage(e.target.value) },
                })}>
          <option value="">— Seite wählen —</option>
          {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <p className="ffb-dyn-hint">
          {resolution === 'manual' && !isPreCheckin
            ? 'Wird angezeigt, wenn die Wartezeit ohne Zuweisung abläuft. Ohne Rückfallseite bleibt der Besucher ohne Inhalt stehen.'
            : 'Wird angezeigt, wenn keine Regel zutrifft. Ohne Rückfallseite kann der Besucher an diesem Schritt ohne Inhalt stehen bleiben.'}
        </p>
      </div>

      {isPreCheckin && (
        <p className="ffb-dyn-hint">
          Im Vorab-Check-in steht bei Zeitbedingungen nur die <strong>geplante</strong>{' '}
          Ankunft zur Verfügung — er kann Tage vor dem Besuch stattfinden.
        </p>
      )}

      {plannedTimeWithoutField && (
        <p className="ffb-dyn-warn">
          ⚠ Eine Regel prüft die geplante Uhrzeit, aber dieser Ablauf erfasst kein
          Uhrzeit-Feld. Diese Bedingung kann nie zutreffen.
        </p>
      )}
    </div>
  )
}

// Shown instead of auto-creating when the user adds an info_page step.
// Lets them pick an existing page (shared across flows) or create a new one.

function InfoPagePicker({ tenantId, apiFetch, onSelect, onCreate, onClose }) {
  const [pages,   setPages]   = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  useEffect(() => {
    apiFetch(`/api/admin/info-pages/${tenantId}`)
      .then(r => r.json())
      .then(data => { setPages(Array.isArray(data) ? data : []); setLoading(false) })
      .catch(() => { setError('Seiten konnten nicht geladen werden'); setLoading(false) })
  }, [tenantId])

  return (
    <div className="ffb-picker-overlay" onClick={onClose}>
      <div className="ffb-picker" onClick={e => e.stopPropagation()}>
        <div className="ffb-picker-header">
          <h3 className="ffb-picker-title">Informationsseite wählen</h3>
          <button className="ffb-btn-icon" onClick={onClose}>✕</button>
        </div>

        <div className="ffb-picker-list">
          <button className="ffb-picker-item ffb-picker-item--new" onClick={onCreate}>
            <span className="ffb-picker-item-label">+ Neue Seite erstellen</span>
            <span className="ffb-picker-item-desc">Eine leere Informationsseite wird angelegt</span>
          </button>

          {loading && <p className="ffb-picker-empty">Lade Seiten…</p>}
          {error   && <p className="ffb-picker-empty">{error}</p>}

          {!loading && !error && pages?.length === 0 && (
            <p className="ffb-picker-empty">Noch keine Informationsseiten vorhanden.</p>
          )}

          {!loading && pages?.map(page => (
            <button
              key={page.id}
              className="ffb-picker-item"
              onClick={() => onSelect(page)}
            >
              <span className="ffb-picker-item-label">{page.name}</span>
              <span className="ffb-picker-item-desc">/{page.slug}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

const FormFlowBuilder = () => {
  const { apiFetch, isOwner, admin } = useAuth()

  const [flowType,       setFlowType]       = useState('pre_checkin')
  const [tenants,        setTenants]        = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [steps,          setSteps]          = useState([])
  const [pageTypes,      setPageTypes]      = useState([])
  const [editingId,      setEditingId]      = useState(null)
  const [showPicker,           setShowPicker]           = useState(false)
  const [showInfoPagePicker,   setShowInfoPagePicker]   = useState(false)
  const [showDataEntryPicker,  setShowDataEntryPicker]  = useState(false)
  const [pendingPageType,      setPendingPageType]      = useState(null)
  const [sourceLang,     setSourceLang]     = useState('de')
  const [loading,        setLoading]        = useState(true)
  const [saving,         setSaving]         = useState(false)
  const [error,          setError]          = useState(null)
  const [success,        setSuccess]        = useState(false)
  const [subFlowTabs,    setSubFlowTabs]    = useState([])
  const [qrValidity,       setQrValidity]       = useState(null)
  const [inheritQr,        setInheritQr]        = useState(true)
  const [parentQrValidity, setParentQrValidity] = useState(null)
  const [requireApproval,  setRequireApproval]  = useState(false)
  const [deliveriesEnabled, setDeliveriesEnabled] = useState(false)

  const dragStepIdx = useRef(null)

  // Build tab tree from FLOW_TYPES (roots) + subFlowTabs (children via parentId)
  const tabTree = React.useMemo(() => {
    const all  = [...FLOW_TYPES, ...subFlowTabs]
    const map  = {}
    all.forEach(t => { map[t.id] = { ...t, children: [] } })
    const roots = []
    all.forEach(t => {
      if (t.parentId && map[t.parentId]) map[t.parentId].children.push(map[t.id])
      else roots.push(map[t.id])
    })
    return roots
  }, [subFlowTabs])

  // ── Load tenants (owner sees all; tenant_admin sees only their own) ────────
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants')
        .then(r => r.json())
        .then(data => {
          const list = Array.isArray(data) ? data : []
          setTenants(list)
          if (list.length > 0) setSelectedTenant(list[0].id)
          else setLoading(false)
        })
        .catch(() => setLoading(false))
    } else {
      // Tenant admin — use their own tenant
      setSelectedTenant(admin.tenant_id)
      setTenants([{ id: admin.tenant_id, name: admin.email }])
    }
  }, [isOwner])

  // ── Whether the selected tenant has Lieferavis switched on ────────────────
  // Only changes a hint in the data-entry editor; the step setting itself can be
  // made either way and the server ignores it while the feature is off.
  useEffect(() => {
    if (!selectedTenant) return
    if (!isOwner) { setDeliveriesEnabled(admin?.features?.deliveries_enabled === true); return }
    apiFetch(`/api/admin/tenant-features/${selectedTenant}`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => setDeliveriesEnabled(data?.features?.deliveries_enabled === true))
      .catch(() => setDeliveriesEnabled(false))
  }, [selectedTenant, isOwner])

  // ── Load subFlowTabs whenever selected tenant changes ─────────────────────
  useEffect(() => {
    if (!selectedTenant) return
    apiFetch(`/api/admin/form-config/${selectedTenant}/terminal`)
      .then(r => r.json())
      .then(data => {
        const terminalSteps = Array.isArray(data.steps) ? data.steps : []
        const flowPickerStep = terminalSteps.find(s => s.type === 'flow_picker')
        if (flowPickerStep?.config?.subFlows) {
          setSubFlowTabs(
            flowPickerStep.config.subFlows.map(sf => ({
              id:       sf.flowType,
              label:    getLabelString(sf.label, 'de'),
              group:    'Kiosk',
              parentId: 'terminal',
            }))
          )
        }
      })
      .catch(() => {})
  }, [selectedTenant])

  // ── Load page types when flow type changes ────────────────────────────────
  useEffect(() => {
    apiFetch(`/api/admin/page-types?flowType=${flowType}`)
      .then(r => r.json())
      .then(data => setPageTypes(Array.isArray(data) ? data : []))
      .catch(() => {})
  }, [flowType])

  // ── Load form config when tenant or flow type changes ─────────────────────
  const loadFormConfig = () => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    setEditingId(null)
    apiFetch(`/api/admin/form-config/${selectedTenant}/${flowType}`)
      .then(r => r.json())
      .then(data => {
        const loadedSteps = Array.isArray(data.steps) ? data.steps : []
        setSteps(loadedSteps)
        const hasOwn = !!data.qrValidity
        // pre_checkin has no parent — always show own settings
        setInheritQr(flowType !== 'pre_checkin' && !hasOwn)
        setQrValidity(data.qrValidity ?? { type: 'day', credits: 2 })
        setRequireApproval(data.requireApproval ?? false)

        // Load parent flow's qr_validity for the "inherit" preview
        // (pre_checkin's underscore is part of its name, not a hierarchy separator — no parent)
        const parentFlowType = flowType !== 'pre_checkin' && flowType.includes('_')
          ? flowType.substring(0, flowType.lastIndexOf('_'))
          : null
        if (parentFlowType) {
          apiFetch(`/api/admin/form-config/${selectedTenant}/${parentFlowType}`)
            .then(r => r.json())
            .then(pd => setParentQrValidity(pd.qrValidity ?? null))
            .catch(() => setParentQrValidity(null))
        } else {
          setParentQrValidity(null)
        }
        // Refresh subFlowTabs from any flow_picker step in this flow
        const flowPickerStep = loadedSteps.find(s => s.type === 'flow_picker')
        if (flowPickerStep?.config?.subFlows) {
          const newTabs = flowPickerStep.config.subFlows.map(sf => ({
            id:       sf.flowType,
            label:    getLabelString(sf.label, 'de'),
            group:    'Kiosk',
            parentId: flowType,
          }))
          if (flowType === 'terminal') {
            // Top-level: replace list entirely (source of truth)
            setSubFlowTabs(newTabs)
          } else {
            // Nested subflow: merge in any tabs not already present
            setSubFlowTabs(prev => {
              const existingIds = new Set(prev.map(t => t.id))
              const toAdd = newTabs.filter(t => !existingIds.has(t.id))
              return toAdd.length > 0 ? [...prev, ...toAdd] : prev
            })
          }
        }
      })
      .catch(() => setError('Konfiguration konnte nicht geladen werden'))
      .finally(() => setLoading(false))
  }

  useEffect(loadFormConfig, [selectedTenant, flowType])

  // ── Drag & drop step reordering ───────────────────────────────────────────
  const handleStepDragStart = (e, idx) => {
    dragStepIdx.current = idx
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleStepDragOver = (e, idx) => {
    e.preventDefault()
    if (dragStepIdx.current === null || dragStepIdx.current === idx) return
    const next = [...steps]
    const [dragged] = next.splice(dragStepIdx.current, 1)
    next.splice(idx, 0, dragged)
    dragStepIdx.current = idx
    setSteps(next)
  }

  const handleStepDrop = () => { dragStepIdx.current = null }

  // ── Step mutations ────────────────────────────────────────────────────────
  const toggleStep = (id) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s))
  }

  const removeStep = (id) => {
    setSteps(prev => prev.filter(s => s.id !== id))
    if (editingId === id) setEditingId(null)
  }

  const updateStepConfig = (id, config) => {
    setSteps(prev => prev.map(s => s.id === id ? { ...s, config } : s))
  }

  const insertStep = (pageType, config) => {
    const newStep = {
      id:      generateId(),
      type:    pageType.type,
      enabled: true,
      config,
    }
    const lastFixedEndIdx = steps.findLastIndex(s => {
      const pt = pageTypes.find(p => p.type === s.type)
      return pt?.fixed && pt?.fixedPosition === 'end'
    })
    const insertAt = lastFixedEndIdx >= 0 ? lastFixedEndIdx : steps.length
    const next = [...steps]
    next.splice(insertAt, 0, newStep)
    setSteps(next)
    setShowPicker(false)
    setShowInfoPagePicker(false)
    setPendingPageType(null)
    if (pageType.type === 'data_entry') setEditingId(newStep.id)
  }

  const addStep = (pageType) => {
    setShowPicker(false)
    if (pageType.type === 'info_page') {
      setPendingPageType(pageType)
      setShowInfoPagePicker(true)
      return
    }
    if (pageType.type === 'data_entry') {
      setPendingPageType(pageType)
      setShowDataEntryPicker(true)
      return
    }
    insertStep(pageType, pageType.defaultConfig ?? {})
  }

  const handleDataEntrySelect = (config) => {
    // Deep-clone the config so the new step is fully independent
    insertStep(pendingPageType, JSON.parse(JSON.stringify(config)))
    setShowDataEntryPicker(false)
    setPendingPageType(null)
  }

  const handleDataEntryCreate = () => {
    insertStep(pendingPageType, pendingPageType.defaultConfig ?? {})
    setShowDataEntryPicker(false)
    setPendingPageType(null)
  }

  const handleInfoPageSelect = (page) => {
    insertStep(pendingPageType, { pageId: page.id, pageName: page.name, pageSlug: page.slug })
  }

  const handleInfoPageCreate = async () => {
    try {
      const slug = `info-seite-${generateId()}`
      const res  = await apiFetch(`/api/admin/info-pages/${selectedTenant}`, {
        method: 'POST',
        body:   JSON.stringify({ name: 'Neue Informationsseite', slug, sourceLang: 'de', blocks: [] }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Fehler beim Erstellen')
      insertStep(pendingPageType, { pageId: data.id, pageName: data.name, pageSlug: data.slug })
    } catch (err) {
      setError(`Informationsseite konnte nicht erstellt werden: ${err.message}`)
      setShowInfoPagePicker(false)
      setPendingPageType(null)
    }
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  // Blocks a save that would strand a visitor at a dynamic info page. Checked here
  // (not only server-side) so the admin sees which step is wrong while looking at it.
  const validateDynamicInfoSteps = () => {
    for (const step of steps) {
      if (step.type !== 'dynamic_info_page' || step.enabled === false) continue
      const cfg   = step.config ?? {}
      const rules = Array.isArray(cfg.rules) ? cfg.rules : []
      const label = cfg.title ? getLabelString(cfg.title, sourceLang) : 'Dynamische Informationsseite'

      // A dynamic step that resolves to nothing leaves the visitor on a dead screen.
      // Required in both modes — manual needs it for the wait timeout.
      if (!cfg.fallback?.pageId) {
        return `${label}: Bitte eine Rückfallseite wählen — ohne sie bleibt der Besucher ohne Inhalt stehen.`
      }

      // Manual mode ignores rules entirely, so rule validation below must not fire on
      // leftover rules from a step that was switched over to manual.
      if (cfg.resolution === 'manual') {
        if (flowType === 'pre_checkin') {
          return `${label}: Manuelle Zuweisung ist im Vorab-Check-in nicht möglich.`
        }
        const secs = cfg.manual?.timeoutSeconds
        if (secs !== undefined && (!Number.isFinite(secs) || secs < 5 || secs > 600)) {
          return `${label}: Wartezeit muss zwischen 5 und 600 Sekunden liegen.`
        }
        continue
      }

      for (let i = 0; i < rules.length; i++) {
        const r = rules[i]
        // Either kind of target satisfies this — a destination brings its own page.
        if (!r.target?.pageId && !r.target?.destinationId)
          return `${label}: Regel ${i + 1} hat kein Ziel.`
        const conds = Array.isArray(r.conditions) ? r.conditions : []
        if (conds.length === 0)             return `${label}: Regel ${i + 1} hat keine Bedingung.`
        for (const c of conds) {
          if (c.source === 'field' && !c.key) {
            return `${label}: Regel ${i + 1} — bitte ein Formularfeld wählen.`
          }
          if (c.source === 'time' && c.key === 'time_of_day') {
            const [start, end] = Array.isArray(c.value) ? c.value : []
            if (!start || !end) return `${label}: Regel ${i + 1} — Uhrzeit-Bereich ist unvollständig.`
            // A window that doesn't end after it starts would silently never match,
            // so it is rejected rather than quietly wrapping past midnight.
            if (end <= start)   return `${label}: Regel ${i + 1} — Ende muss nach dem Start liegen.`
          }
          if (c.source === 'time' && c.key === 'weekday' && !(c.value || []).length) {
            return `${label}: Regel ${i + 1} — bitte mindestens einen Wochentag wählen.`
          }
          // "Actual" arrival has no meaning in pre-checkin, which can happen days early.
          if (c.source === 'time' && flowType === 'pre_checkin' && (c.basis || 'actual') === 'actual') {
            return `${label}: Im Vorab-Check-in ist nur die geplante Ankunftszeit möglich.`
          }
        }
      }
    }
    return null
  }

  const handleSave = async () => {
    const validationError = validateDynamicInfoSteps()
    if (validationError) { setError(validationError); return }

    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      const res  = await apiFetch(`/api/admin/form-config/${selectedTenant}/${flowType}`, {
        method: 'PUT',
        body:   JSON.stringify({ steps, sourceLang, qrValidity: inheritQr ? null : qrValidity, requireApproval }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen')

      // Sync info_page names to the info_pages table
      const infoPageSteps = steps.filter(s => s.type === 'info_page' && s.config?.pageId && s.config?.pageName)
      await Promise.all(infoPageSteps.map(s =>
        apiFetch(`/api/admin/info-pages/${selectedTenant}/${s.config.pageId}`, {
          method: 'PATCH',
          body:   JSON.stringify({
            name: s.config.pageName,
            ...(s.config.pageSlug ? { slug: s.config.pageSlug } : {}),
          }),
        })
      ))

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)

      // After saving, refresh subFlowTabs from any flow_picker step in the saved flow
      const flowPickerStep = steps.find(s => s.type === 'flow_picker')
      if (flowPickerStep?.config?.subFlows) {
        const newTabs = flowPickerStep.config.subFlows.map(sf => ({
          id:       sf.flowType,
          label:    getLabelString(sf.label, 'de'),
          group:    'Kiosk',
          parentId: flowType,
        }))
        if (flowType === 'terminal') {
          setSubFlowTabs(newTabs)
        } else {
          setSubFlowTabs(prev => {
            const existingIds = new Set(prev.map(t => t.id))
            const toAdd = newTabs.filter(t => !existingIds.has(t.id))
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev
          })
        }
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  const getPageTypeDef = (type) => pageTypes.find(pt => pt.type === type)

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Flow Builder</h1>
          <p>Seiten und Felder des Besucher-Flows konfigurieren</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ Gespeichert</div>}

      {/* Flow type tabs — tree: each node is a column, children appear below their parent */}
      <div className="ffb-tabs">
        {(function renderNodes(nodes, depth) {
          return nodes.map((ft, idx) => {
            const showGroup = depth === 0 && ft.group && (idx === 0 || nodes[idx - 1].group !== ft.group)
            return (
              <React.Fragment key={ft.id}>
                {showGroup && <span className="ffb-tab-group-label">{ft.group}</span>}
                <div className="ffb-tab-node">
                  <button
                    className={`ffb-tab${flowType === ft.id ? ' active' : ''}`}
                    onClick={() => setFlowType(ft.id)}
                  >
                    {ft.label}
                  </button>
                  {ft.children?.length > 0 && (
                    <div className="ffb-tab-children">
                      <span className="ffb-tab-tree-branch">↳</span>
                      {renderNodes(ft.children, depth + 1)}
                    </div>
                  )}
                </div>
              </React.Fragment>
            )
          })
        })(tabTree, 0)}
      </div>

      {/* Tenant selector (owner only) */}
      {isOwner && tenants.length > 0 && (
        <div className="card ffb-tenant-card">
          <div className="form-group ffb-tenant-form-group">
            <label>Mandant</label>
            <select
              className="form-control"
              value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}
            >
              {tenants.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="card">
        <div className="section-title">Seiten-Reihenfolge</div>
        <p className="sm-hint">
          Ziehen zum Umsortieren. Fixierte Seiten können nicht entfernt werden.
          Klicke auf ✏️ um Felder einer Dateneingabe-Seite zu bearbeiten.
        </p>

        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : (
          <>
            <div className="ffb-step-list">
              {steps.map((step, idx) => {
                const def            = getPageTypeDef(step.type)
                const isFixed        = def?.fixed ?? false
                const isConfigurable = def?.configurable ?? false
                const isEditing      = editingId === step.id
                const isFlowPicker   = step.type === 'flow_picker'
                const isQrCheckin    = step.type === 'qr_checkin' || step.type === 'checkout_scan'
                const isDocScan      = step.type === 'document_scan'
                const isAddressCheck = step.type === 'address_check'
                const isConfirmation = step.type === 'confirmation'
                const isSignature    = step.type === 'signature'
                const isDynamicInfo  = step.type === 'dynamic_info_page'
                const isDirectCall   = step.type === 'direct_call'

                return (
                  <React.Fragment key={step.id}>
                    <div
                      className={`ffb-step-row${isFixed ? ' ffb-step-row--fixed' : ''}`}
                      draggable={!isFixed}
                      onDragStart={e => !isFixed && handleStepDragStart(e, idx)}
                      onDragOver={e => !isFixed && handleStepDragOver(e, idx)}
                      onDrop={handleStepDrop}
                    >
                      <span
                        className={`ffb-drag-handle${isFixed ? ' ffb-drag-handle--disabled' : ''}`}
                        title={isFixed ? '' : 'Ziehen zum Verschieben'}
                      >≡</span>

                      <div className="ffb-step-info">
                        {step.type === 'info_page' ? (
                          <>
                            <input
                              className="form-control ffb-infopage-name-input"
                              value={step.config?.pageName ?? ''}
                              onChange={e => updateStepConfig(step.id, { ...step.config, pageName: e.target.value })}
                              placeholder="Seitenname (z. B. Sicherheitshinweise Halle A)"
                            />
                            <input
                              className="form-control ffb-infopage-slug-input"
                              value={step.config?.pageSlug ?? ''}
                              onChange={e => updateStepConfig(step.id, { ...step.config, pageSlug: e.target.value })}
                              placeholder="Slug (z. B. sicherheitshinweise-halle-a)"
                            />
                            <div className="ffb-step-desc">
                              Inhalt unter „Informationsseiten" bearbeiten
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="ffb-step-label">
                              {def?.label ?? step.type}
                              {step.config?.title ? ` — ${getLabelString(step.config.title, sourceLang)}` : ''}
                            </div>
                            <div className="ffb-step-desc">{def?.description ?? ''}</div>
                          </>
                        )}
                      </div>

                      <div className="ffb-step-actions">
                        {isFixed ? (
                          <>
                            {isConfigurable && (
                              <button
                                className="ffb-btn-icon"
                                title="Unterflows bearbeiten"
                                onClick={() => setEditingId(isEditing ? null : step.id)}
                              >✏️</button>
                            )}
                            <span className="ffb-badge-fixed">Fixiert</span>
                          </>
                        ) : (
                          <>
                            {isConfigurable && (
                              <button
                                className="ffb-btn-icon"
                                title="Felder bearbeiten"
                                onClick={() => setEditingId(isEditing ? null : step.id)}
                              >✏️</button>
                            )}

                            <label className="toggle sm-toggle" title={step.enabled ? 'Deaktivieren' : 'Aktivieren'}>
                              <input
                                type="checkbox"
                                checked={step.enabled}
                                onChange={() => toggleStep(step.id)}
                              />
                              <span className="toggle-slider" />
                            </label>

                            <button
                              className="ffb-btn-icon ffb-btn-icon--danger"
                              title="Seite entfernen"
                              onClick={() => removeStep(step.id)}
                            >🗑</button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Repeat-for-field — shown for every non-fixed, non-picker, non-scan step.
                        Not for Direktanruf: calling the same contact once per repetition
                        makes no sense. */}
                    {!isFixed && !isFlowPicker && !isQrCheckin && !isDocScan && !isAddressCheck && !isConfirmation && !isDirectCall && (
                      <>
                        <MultiplyByControl
                          config={step.config ?? {}}
                          onChange={cfg => updateStepConfig(step.id, cfg)}
                          allSteps={steps}
                          sourceLang={sourceLang}
                        />
                        <GroupControl
                          step={step}
                          config={step.config ?? {}}
                          onChange={cfg => updateStepConfig(step.id, cfg)}
                          allSteps={steps}
                          pageTypes={pageTypes}
                        />
                      </>
                    )}

                    {/* Inline editor — FlowPickerEditor, QrCheckinEditor, or FieldEditor */}
                    {isEditing && isFlowPicker && (
                      <FlowPickerEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                        onAutoSave={updatedConfig => {
                          const updatedSteps = steps.map(s =>
                            s.id === step.id ? { ...s, config: updatedConfig } : s
                          )
                          apiFetch(`/api/admin/form-config/${selectedTenant}/${flowType}`, {
                            method: 'PUT',
                            body:   JSON.stringify({ steps: updatedSteps, sourceLang }),
                          })
                            .then(r => { if (!r.ok) throw new Error() })
                            .catch(() => setError('Unterflow konnte nicht gespeichert werden — bitte manuell speichern'))
                        }}
                        apiFetch={apiFetch}
                        tenantId={selectedTenant}
                        parentFlowType={flowType}
                        sourceLang={sourceLang}
                      />
                    )}
                    {isEditing && isQrCheckin && (
                      <QrCheckinEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                        stepType={step.type}
                        sourceLang={sourceLang}
                      />
                    )}
                    {isEditing && isDynamicInfo && (
                      <DynamicInfoPageEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                        allSteps={steps}
                        sourceLang={sourceLang}
                        tenantId={selectedTenant}
                        apiFetch={apiFetch}
                        flowType={flowType}
                      />
                    )}
                    {isEditing && isDocScan && (
                      <DocumentScanEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                        sourceLang={sourceLang}
                      />
                    )}
                    {isEditing && isDirectCall && (
                      <DirectCallEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                        sourceLang={sourceLang}
                        tenantId={selectedTenant}
                        apiFetch={apiFetch}
                      />
                    )}
                    {isEditing && isAddressCheck && (
                      <div className="ffb-editor-panel">
                        <div className="form-group">
                          <label>Erwartete Lieferadresse</label>
                          <input
                            className="form-control"
                            placeholder="z. B. Musterstraße 12, 76131 Karlsruhe"
                            value={step.config?.expectedAddress ?? ''}
                            onChange={e => updateStepConfig(step.id, { ...step.config, expectedAddress: e.target.value })}
                          />
                          <p className="ffb-field-hint">Der Fahrer muss diese Adresse eingeben. Groß-/Kleinschreibung und Bindestriche werden ignoriert.</p>
                        </div>
                      </div>
                    )}
                    {isEditing && isConfirmation && (
                      <ConfirmationEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                        sourceLang={sourceLang}
                        tenantId={selectedTenant}
                      />
                    )}
                    {isEditing && isSignature && (
                      <SignatureEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                      />
                    )}
                    {isEditing && !isFlowPicker && !isQrCheckin && !isDocScan && !isAddressCheck && !isConfirmation && !isSignature && !isDirectCall && (
                      <FieldEditor
                        config={step.config ?? {}}
                        onChange={cfg => updateStepConfig(step.id, cfg)}
                        sourceLang={sourceLang}
                        deliveryCheck={step.type === 'data_entry' && flowType !== 'pre_checkin'
                          ? { enabled: deliveriesEnabled }
                          : null}
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </div>

            {/* Add page button */}
            <div className="ffb-add-row">
              <button className="btn btn-ghost" onClick={() => setShowPicker(true)}>
                + Seite hinzufügen
              </button>
            </div>

            {/* Source language + save */}
            <div className="ffb-save-row">
              <div className="ffb-lang-row">
                <label className="ffb-lang-label">Eingabesprache</label>
                <select
                  className="form-control ffb-select-auto"
                  value={sourceLang}
                  onChange={e => setSourceLang(e.target.value)}
                >
                  {[
                    ['de','Deutsch'],['en','English'],['es','Español'],['it','Italiano'],
                    ['pl','Polski'],['nl','Nederlands'],['da','Dansk'],['fr','Français'],
                    ['hu','Magyar'],['hr','Hrvatski'],['pt','Português'],['cs','Čeština'],
                    ['bg','Български'],['et','Eesti'],['fi','Suomi'],['el','Ελληνικά'],
                    ['lv','Latviešu'],['lt','Lietuvių'],['no','Norsk'],['ru','Русский'],
                    ['sr','Srpski'],['sk','Slovenčina'],['sl','Slovenščina'],['sv','Svenska'],
                    ['tr','Türkçe'],['uk','Українська'],
                  ].map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving
                  ? <><span className="spinner spinner-sm" /> Übersetzen & Speichern...</>
                  : 'Speichern & Übersetzen'}
              </button>
            </div>

            {/* QR-Code validity — shown for pre_checkin and all terminal subflows */}
            {(flowType === 'pre_checkin' || flowType.startsWith('terminal_')) && qrValidity && (
              <div className="card ffb-qr-card">
                <p className="section-title ffb-qr-title">QR-Code Einstellungen</p>

                {/* Inherit toggle — only shown for terminal subflows, not pre_checkin */}
                {flowType.startsWith('terminal_') && (
                  <div className="row-between ffb-inherit-row">
                    <div>
                      <strong className="ffb-inherit-label">Von übergeordnetem Flow erben</strong>
                      {inheritQr && parentQrValidity && (
                        <p className="ffb-inherit-desc">
                          Gültigkeit: <strong>{parentQrValidity.type === 'day' ? 'Besuchstag' : parentQrValidity.type === 'days' ? `${parentQrValidity.days} Tage` : 'Festes Datum'}</strong>
                          {' · '}Credits: <strong>{parentQrValidity.credits ?? 2}</strong>
                        </p>
                      )}
                      {inheritQr && !parentQrValidity && (
                        <p className="ffb-inherit-desc">
                          Kein übergeordneter Flow konfiguriert — Standard wird verwendet.
                        </p>
                      )}
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={inheritQr}
                        onChange={e => setInheritQr(e.target.checked)} />
                      <span className="toggle-slider" />
                    </label>
                  </div>
                )}

                {/* Own settings — hidden when inheriting */}
                {!inheritQr && (
                  <>
                    <div className="form-group">
                      <label>Gültigkeit</label>
                      <select
                        className="form-control"
                        value={qrValidity.type}
                        onChange={e => setQrValidity(v => ({ ...v, type: e.target.value }))}
                      >
                        <option value="day">Nur Besuchstag</option>
                        <option value="days">Anzahl Tage ab Check-in</option>
                        <option value="permanent">Festes Ablaufdatum</option>
                      </select>
                    </div>

                    {qrValidity.type === 'days' && (
                      <div className="form-group">
                        <label>Anzahl Tage</label>
                        <input
                          type="number" min="1" max="3650"
                          className="form-control ffb-input-sm"
                          value={qrValidity.days ?? 1}
                          onChange={e => setQrValidity(v => ({ ...v, days: parseInt(e.target.value) || 1 }))}
                        />
                      </div>
                    )}

                    {qrValidity.type === 'permanent' && (
                      <div className="form-group">
                        <label>Ablaufdatum</label>
                        <input
                          type="date"
                          className="form-control ffb-input-md"
                          value={qrValidity.validUntil ?? ''}
                          onChange={e => setQrValidity(v => ({ ...v, validUntil: e.target.value }))}
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>Scanner-Credits</label>
                      <select
                        className="form-control"
                        value={qrValidity.credits ?? 2}
                        onChange={e => setQrValidity(v => ({ ...v, credits: parseInt(e.target.value) }))}
                      >
                        <option value={2}>2 — Eingang + Ausgang (selber QR)</option>
                        <option value={1}>1 — Nur Eingang (separater Ausgangs-QR)</option>
                        <option value={0}>0 — Unbegrenzt (Mitarbeiter / Langzeit)</option>
                      </select>
                      <p className="ffb-field-hint">
                        Anzahl Scans am DAS-Scanner pro QR-Code.
                      </p>
                    </div>

                    <div className="form-group ffb-form-group-last">
                      <label>Zugangsart</label>
                      <select
                        className="form-control"
                        value={qrValidity.accessType ?? ''}
                        onChange={e => setQrValidity(v => ({ ...v, accessType: e.target.value || null }))}
                      >
                        <option value="">Keine Einschränkung</option>
                        <option value="foot">Immer Fußgänger</option>
                        <option value="vehicle">Immer Fahrzeug</option>
                        <option value="auto">Automatisch (Kennzeichen)</option>
                      </select>
                      <p className="ffb-field-hint">
                        „Automatisch": Fahrzeug-Zugang wenn Kennzeichen angegeben, sonst Fußgänger.
                      </p>
                    </div>
                  </>
                )}

                <div className="row-between ffb-inherit-row" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <div>
                    <strong className="ffb-inherit-label">Genehmigung erforderlich</strong>
                    <p className="ffb-inherit-desc">
                      Check-ins in diesem Flow müssen vom Admin genehmigt werden, bevor der QR-Code ausgegeben wird.
                    </p>
                  </div>
                  <label className="toggle">
                    <input type="checkbox" checked={requireApproval}
                      onChange={e => setRequireApproval(e.target.checked)} />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add page picker modal */}
      {showPicker && (
        <AddPagePicker
          pageTypes={pageTypes}
          existingSteps={steps}
          onAdd={addStep}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* Info page picker — choose existing or create new */}
      {showInfoPagePicker && (
        <InfoPagePicker
          tenantId={selectedTenant}
          apiFetch={apiFetch}
          onSelect={handleInfoPageSelect}
          onCreate={handleInfoPageCreate}
          onClose={() => { setShowInfoPagePicker(false); setPendingPageType(null) }}
        />
      )}

      {/* DataEntry picker — clone from existing sub-flow or start blank */}
      {showDataEntryPicker && (
        <DataEntryPicker
          tenantId={selectedTenant}
          apiFetch={apiFetch}
          onSelect={handleDataEntrySelect}
          onCreate={handleDataEntryCreate}
          onClose={() => { setShowDataEntryPicker(false); setPendingPageType(null) }}
        />
      )}

      {/* History covers every flow type for this tenant, not just the one
          currently open — the GET /form-config response doesn't include the
          row's own id, so the panel can't be scoped down to just flowType. */}
      {selectedTenant && (
        <VersionHistoryPanel table="form_configs" tenantId={selectedTenant} onRestored={loadFormConfig} />
      )}
    </div>
  )
}

export default FormFlowBuilder
